/**
 * Ukrainian.
 *
 * Present for the same reason as on the till: a large share of Czech hospitality
 * staff read Ukrainian more comfortably than Czech, and a manager's screen is
 * not only read by the owner.
 */

import type { Messages } from "./en";

export const uk: { locale: string; messages: Messages } = {
  locale: "uk",
  messages: {
    // --- Sign-in -----------------------------------------------------------

    // --- Role names ---------------------------------------------------------
    // The roster showed raw identifiers — "manager", "staff" — which is the same
    // database key on a screen that made the kitchen header read "KITCHEN".
    "role.owner": "Власник",
    "role.admin": "Адміністратор",
    "role.manager": "Менеджер",
    "role.shift_lead": "Старший зміни",
    "role.staff": "Офіціант",
    "role.viewer": "Перегляд",
    "signIn.title": "Вхід",
    "signIn.subtitle": "Введіть свій PIN — той самий, що й на касі.",
    "signIn.staff": "Хто ви",
    "signIn.staffPlaceholder": "напр. Eva S.",
    "signIn.staffHint": "Імʼя, як його показує каса.",
    "signIn.pin": "PIN",
    "signIn.submit": "Увійти",
    "signIn.working": "Входжу…",
    "signIn.badCredentials": "Невірне імʼя або PIN.",
    "signIn.notBackOffice": "Ця роль не має доступу до офісу.",
    "signIn.unreachable": "Сервер не відповідає. Перевірте зʼєднання.",
    "signIn.tooMany": "Забагато спроб. Спробуйте ще раз за {minutes} хв.",
    "signIn.signedInAs": "Ви увійшли як",
    "signIn.signOut": "Вийти",
    "signIn.expiresAt": "Сесія завершиться",
    // --- Command palette ---------------------------------------------------
    "palette.label": "Перейти до екрана",
    "palette.placeholder": "Пошук екрана…",
    "palette.open": "Пошук",
    "palette.here": "ви тут",
    "palette.nothing": "Такого тут немає",
    "palette.hintMove": "рух",
    "palette.hintOpen": "відкрити",
    "palette.hintClose": "закрити",
    "shell.menu": "Меню",
    "shell.closeMenu": "Закрити меню",

    "app.title": "Офіс",
    "nav.today": "Сьогодні",
    "nav.sales": "Виручка",
    "nav.checks": "Чеки",
    "nav.shifts": "Зміни",
    "nav.kitchen": "Кухня",
    "nav.fiscal": "Податки",
    "nav.tableCards": "Картки на столи",
    "tableCards.lede": "Надрукуйте по одній картці на стіл. Гість сканує її, відкриває власний рахунок і замовляє — ніхто не мусить підходити.",
    "tableCards.tablesLabel": "Столи",
    "tableCards.placeholder": "12\n13\nтераса 3",
    "tableCards.hint": "Один стіл на рядок, так як ви їх називаєте. Карток:",
    "tableCards.duplicates": "Двічі вказано:",
    "tableCards.print": "Надрукувати картки",
    "tableCards.expires": "Картки перестануть працювати",
    "tableCards.callToAction": "Скануйте та замовляйте",
    "tableCards.modeWarning": "Цей заклад відкриває рахунки на касі. Картки надрукуються, але доки ви не зміните модель у Налаштуваннях, гість побачить «зверніться до офіціанта».",
    "tableCards.noBaseUrl": "Не вказано, де працює застосунок для гостей, тож картка несе лише код. Камера запропонує скопіювати його, а не відкрити.",
    "nav.menu": "Меню",
    "nav.staff": "Люди",
    "nav.settings": "Налаштування",
    "nav.group.running": "Робота закладу",
    "nav.group.money": "Гроші",
    "nav.group.setup": "Налаштування",
    "nav.group.soon": "Готуємо",

    "common.loading": "Завантаження…",
    "common.failed": "Не вдалося завантажити",
    "common.retry": "Спробувати ще раз",
    "common.empty": "Поки що порожньо",
    "common.today": "Сьогодні",
    "common.week": "Останні 7 днів",
    "common.month": "Останні 30 днів",
    "common.custom": "Власний",
    "common.from": "Від",
    "common.to": "До",
    "common.unreachable": "Сервер не відповів. Це не нулі — значення невідомі.",
    "common.of": "з",
    "common.total": "Разом",
    "common.date": "Дата",
    "common.time": "Час",
    "common.none": "Немає",

    "today.takings": "Виручка за сьогодні",
    "today.checks": "Чеки",
    "today.average": "Середній чек",
    "today.open": "Відкриті столи",
    "today.openMoney": "На відкритих столах",
    "today.vat": "ПДВ",
    "today.payments": "Способи оплати",
    "today.quiet": "Сьогодні ще нічого не продано.",
    "today.quietHint": "Торговий день починається о 4:00.",

    "sales.heading": "Виручка",
    "sales.day": "Торговий день",
    "sales.checkCount": "Чеків",
    "sales.revenue": "Виручка",
    "sales.breakdown": "Розбивка виторгу",
    "sales.tab.items": "Позиції",
    "sales.tab.staff": "Офіціанти",
    "sales.tab.hours": "Години",
    "sales.category": "Категорія",
    "sales.item": "Позиція",
    "sales.quantity": "Кількість",
    "sales.discounts": "Знижки",
    "sales.server": "Офіціант",
    "sales.voids": "Сторно",
    "sales.hour": "Година",
    "sales.other": "Інше",
    "sales.vat": "ПДВ",
    "sales.net": "Без ПДВ",
    "sales.covers": "Гостей",
    "sales.perCover": "На гостя",
    "sales.coversHint": "{n} гостей за столами — продажі біля стійки не враховано",
    "sales.average": "Середнє",
    "sales.tips": "Чайові",
    "sales.rounding": "Округлення",
    "sales.byRate": "За ставкою ПДВ",
    "sales.byTender": "За способом оплати",
    "sales.gaps": "Дні без виручки",
    "sales.gapsWhy":
      "Пропущений торговий день не дає закрити період — ПДВ на виході прив'язаний до дати постачання, тож поданий із запізненням день потребує уточнюючої декларації.",
    "sales.tipsNotRevenue": "Чайові — це зобов'язання перед персоналом, а не виручка.",

    "checks.heading": "Чеки",
    "checks.number": "Номер",
    "checks.table": "Стіл",
    "checks.opened": "Відкрито",
    "checks.closed": "Закрито",
    "checks.state": "Стан",
    "checks.stateOpen": "Відкритий",
    "checks.openSince": "Відкритий з {day}",
    "checks.stateSettled": "Закритий",
    "checks.outstanding": "Залишок до сплати",
    "checks.staff": "Обслуговував",
    "checks.lines": "Позиції",
    "checks.voided": "Сторновано",
    "voidReason.RUNG_IN_ERROR": "Помилково додано",
    "voidReason.GUEST_CHANGED_MIND": "Гість передумав",
    "voidReason.SENT_BACK": "Повернуто з кухні",
    "voidReason.SPILLED": "Розлито або впало",
    "voidReason.OTHER": "Інша причина",
    "checks.tenders": "Платежі",
    "checks.anomalies": "Що не зійшлося",
    "checks.noNumber": "Без номера чека",
    "checks.tab": "Рахунок {n}",
    "checks.fiscal.reported": "Передано податковій",
    "checks.fiscal.test": "Підтверджено ТЕСТОВОЮ службою",
    "checks.fiscal.testWhy": "Тестове підтвердження нічого не передає. Цей продаж не зареєстровано.",
    "checks.fiscal.refused": "Відхилено",
    "checks.fiscal.pending": "Ще не передано",
    "checks.fiscal.none": "Для цього продажу подання ніколи не ставилося в чергу",

    "shifts.heading": "Зміни",
    "shifts.cashier": "Касир",
    "shifts.expected": "Очікувано",
    "shifts.counted": "Пораховано",
    "shifts.variance": "Різниця",
    "shifts.open": "Ще відкрита",
    "shifts.uncounted": "Ще не рахували",
    "shifts.over": "Надлишок",
    "shifts.short": "Нестача",
    "shifts.exact": "Точно",
    "shifts.ok": "У межах допуску",
    "shifts.watch": "Варто подивитися",
    "shifts.alert": "Потрібно розібратися",
    "shifts.breakdown": "З чого складається очікувана сума",
    "shifts.breakdownShort": "Розбивка",
    "shifts.openingFloatMinor": "Початкова готівка",
    "shifts.cashSalesMinor": "Виручка готівкою",
    "shifts.cashRefundsMinor": "Повернення готівкою",
    "shifts.roundingMinor": "Округлення",
    "shifts.payInsMinor": "Внески",
    "shifts.payOutsMinor": "Видатки",
    "shifts.safeDropsMinor": "Здано в сейф",
    "shifts.cashTipsMinor": "Чайові готівкою",
    "shifts.tipOutsMinor": "Виплачені чайові",
    "shifts.movements": "Рухи готівки",
    "shifts.overIsNotSafe":
      "Постійний надлишок повідомляємо так само гучно, як нестачу: зазвичай це готівка, вийнята з каси й ще не винесена.",
    "shifts.unattributed":
      "Готівка, проведена без відкритої каси. Її немає в жодному Z-звіті нижче й порівняти її ні з чим.",
    "shifts.ambiguous": "з них із двома відкритими касами одночасно",

    "kitchen.heading": "Кухня",
    "kitchen.median": "Медіана часу до видачі",
    "kitchen.p90": "Найповільніша десята частина",
    "kitchen.tickets": "Тікети",
    "kitchen.served": "Видано",
    "kitchen.station": "Станція",
    "kitchen.late": "Запізнілі тікети",
    "kitchen.whyPercentile": "Перцентилі, а не середнє — гість переживає саме хвіст.",

    "fiscal.heading": "Податкова",
    "fiscal.eet": "EET",
    "fiscal.notSetUp": "Не налаштовано",
    "fiscal.notSetUpWhy": "Обов’язково з 1. 1. 2027. DIČ платника та номер закладу вводяться в Налаштуваннях.",
    "fiscal.credentials": "Підписний сертифікат",
    "fiscal.present": "Встановлено",
    "fiscal.absent": "Відсутній",
    "fiscal.absentWhy": "Без підписного сертифіката жоден продаж не буде надіслано.",
    "fiscal.oldestPending": "Найстаріша ще не звітована виручка",
    "fiscal.quarantined": "Відхилено остаточно",
    "fiscal.attempts": "спроб",
    "fiscal.andMore": "і ще стільки не показано:",
    "fiscal.showRows": "Показати інші чеки",
    "fiscal.kind.NO_TILL_ID": "Каса не має фіскального ID",
    "fiscal.kind.NO_RECEIPT_NUMBER": "Рахунок не має номера чека",
    "fiscal.kind.NUMBER_OUTSIDE_LEASE": "Номер чека поза виділеним діапазоном",
    "fiscal.kind.NUMBER_ALREADY_ASSIGNED": "Номер чека вже належить іншому рахунку",
    "fiscal.playground": "Підтверджено тестовою службою",
    "fiscal.playgroundWhy":
      "Тестове підтвердження нічого не закриває. Ці продажі виглядають звітованими, але не є ними.",
    "fiscal.unreportable": "Неможливо звітувати",
    "fiscal.clean": "Нічого не очікує й нічого не відхилено.",
    "fiscal.numbering": "Нумерація чеків",
    "fiscal.gaps": "Пропуски",
    "fiscal.gapsClean": "Пропусків немає — кожен виданий номер використано або повернено.",
    "fiscal.issued": "Видано",

    "menu.heading": "Меню",

    "menu.tab.items": "Позиції",

    "menu.tab.categories": "Категорії",

    "menu.tab.groups": "Гарніри й опції",

    "menu.tab.stations": "Станції",

    "menu.search": "Пошук у меню",

    "menu.allCategories": "Усе",

    "menu.uncategorised": "Без категорії",

    "menu.category": "Категорія",

    "menu.newItem": "Нова позиція",

    "menu.newCategory": "Нова категорія",

    "menu.newGroup": "Нова група",

    "menu.newStation": "Нова станція",

    "menu.editItem": "Редагувати",

    "menu.off": "Вимкнено",

    "menu.switchOff": "Вимкнути",

    "menu.switchOn": "Увімкнути",

    "menu.empty": "Тут поки нічого немає.",

    "menu.saved": "Збережено. На каси потрапить після публікації.",

    "menu.saveFailed": "Не вдалося зберегти: {detail}",

    "menu.priceHint": "З ПДВ, як її бачить гість.",

    "menu.rateAfterPublish": "після публікації",

    "menu.itemCount": "Позицій: {count}",

    "menu.usedBy": "У позиціях: {count}",

    "menu.moveUp": "Вище",

    "menu.moveDown": "Нижче",

    "menu.remove": "Прибрати",

    "menu.addOption": "Додати варіант",

    "menu.required": "Обовʼязково",

    "menu.optional": "На вибір",

    "menu.noGroups": "Гарніри й опції — напр. «Příloha» чи «Bez cibule» — каса пропонує під час пробиття. Обовʼязкові відкриваються самі.",

    "menu.taxCategory": "Податкова категорія",

    "menu.tax.FOOD": "Їжа",

    "menu.tax.NON_ALC_BEVERAGE": "Безалкогольний напій",

    "menu.tax.ALCOHOL": "Алкоголь",

    "menu.tax.TAP_WATER": "Вода з-під крана",

    "menu.tax.MILK_BEVERAGE": "Молочний напій",

    "menu.tax.NON_FOOD": "Непродовольчий товар",
    "menuImport.open": "Імпорт із таблиці",
    "menuImport.title": "Імпорт меню з таблиці",
    "menuImport.lead": "Файл CSV — з Excel через «Зберегти як CSV» або експорт зі Storyous. Позиції додаються до чернетки; каси їх не продають, доки ви не опублікуєте меню.",
    "menuImport.file": "Файл",
    "menuImport.choose": "Вибрати файл…",
    "menuImport.noFile": "Файл не вибрано",
    "menuImport.unreadable": "Не вдалося прочитати файл як таблицю.",
    "menuImport.header": "Перший рядок — заголовки",
    "menuImport.column.name": "Назва",
    "menuImport.column.price": "Ціна з ПДВ",
    "menuImport.column.category": "Категорія",
    "menuImport.column.vat": "Ставка ПДВ",
    "menuImport.column.none": "—",
    "menuImport.columnN": "Стовпець {n}",
    "menuImport.items": "Позицій",
    "menuImport.station": "Станція",
    "menuImport.tax": "Податок",
    "menuImport.uncategorised": "Без категорії",
    "menuImport.problems": "Ці рядки не буде додано",
    "menuImport.problem.name": "немає назви",
    "menuImport.problem.price": "не вдається прочитати ціну",
    "menuImport.taxHint": "Податок залежить від того, чим є позиція. Ставку з файлу ми лише порівнюємо із законом: за столом пиво й Kofola мають 21 %, але алкоголь — лише пиво.",
    "menuImport.mismatches": "Ставка у файлі не збігається із законом — позиції буде додано, перевірте їх",
    "menuImport.mismatch": "у файлі {file} %, за столом {rate} %",
    "menuImport.line": "рядок {line}",
    "menuImport.more": "і ще {count}",
    "menuImport.add": "Додати до чернетки ({count})",
    "menuImport.adding": "Додаю…",
    "menuImport.done": "Додано до чернетки: {created}.",
    "menuImport.skipped": "Уже є в меню, пропущено: {count}.",
    "menuImport.publishHint": "Перевірте їх і опублікуйте меню — до того каси їх не продають.",
    "menuImport.close": "Готово",
    "setup.heading": "Перш ніж почати продавати",
    "setup.progress": "Готово {done} з {total}",
    "setup.hide": "Сховати",
    "setup.open": "Відкрити",
    "setup.step.company": "Фірма на чеку — назва та IČO",
    "setup.step.menu": "Меню, опубліковане на каси",
    "setup.step.tables": "Столи",
    "setup.step.people": "Люди та їхні PIN",
    "setup.step.publish": "Заклад, опублікований на каси",
    "setup.step.devices": "Спарені каси та кухонні екрани",
    "setup.step.pohoda": "Каси в Pohoda (для бухгалтера)",
    "menuImport.failed": "Імпорт не вдався: {detail}",

    "menu.field.name": "Назва",

    "menu.field.price": "Ціна для гостя",

    "menu.field.description": "Опис",

    "menu.field.ageRestricted": "Лише 18+",

    "menu.field.active": "У меню",

    "menu.field.groups": "Гарніри й опції",

    "menu.field.min": "Обрати щонайменше",

    "menu.field.max": "Обрати щонайбільше",

    "menu.field.maxUnlimited": "без обмежень",

    "menu.field.options": "Варіанти",

    "menu.field.optionName": "Варіант",

    "menu.field.priceDelta": "Доплата",

    "menu.field.maxQuantity": "Макс. шт.",

    "menu.problem.name": "Групі потрібна назва.",

    "menu.problem.optionName": "Кожному варіанту потрібна назва.",

    "menu.problem.price": "Доплата має бути сумою, напр. 25 або 25,50.",

    "menu.problem.tooFew": "Вимагає більше виборів, ніж пропонує — позицію з нею не можна пробити.",

    "menu.problem.max": "Максимум має бути цілим числом, не меншим за мінімум.",

    "allergen.GLUTEN": "Глютен",

    "allergen.CRUSTACEANS": "Ракоподібні",

    "allergen.EGGS": "Яйця",

    "allergen.FISH": "Риба",

    "allergen.PEANUTS": "Арахіс",

    "allergen.SOYBEANS": "Соя",

    "allergen.MILK": "Молоко",

    "allergen.NUTS": "Горіхи",

    "allergen.CELERY": "Селера",

    "allergen.MUSTARD": "Гірчиця",

    "allergen.SESAME": "Кунжут",

    "allergen.SULPHITES": "Сульфіти",

    "allergen.LUPIN": "Люпин",

    "allergen.MOLLUSCS": "Молюски",
    "menu.edit": "Змінити ціну",
    "menu.editing": "Ціна, яку бачить гість, з ПДВ.",
    "menu.save": "Зберегти",
    "menu.cancel": "Назад",
    "menu.available": "У продажу",
    "menu.soldOut": "Закінчилось",
  "menu.availability": "Наявність",
  "menu.availabilityFailed": "Не вдалося завантажити, чого зараз немає. Колонка «Наявність» може бути застарілою.",
  "menu.markSoldOut": "Позначити, що закінчилося",
  "menu.soldOutReason": "Причина",
  "menu.soldOutDefault": "Закінчилося",
  "menu.soldOutConfirm": "Зняти з меню",
  "menu.backOn": "Повернути в меню",
    "menu.draft": "Зміни набудуть чинності після публікації.",
    "menu.publish": "Опублікувати нову версію",
    "menu.publishing": "Публікую…",
    "menu.published": "Опубліковано як версію {version}. Каси отримають її за хвилину.",
    "menu.editFailed": "Не вдалося зберегти зміну.",
    "menu.version": "Версія",
    "menu.item": "Позиція",
    "menu.station": "Готує",
    "menu.dineIn": "На місці",
    "menu.takeaway": "З собою",
    "menu.rate": "ПДВ",
    "menu.allergens": "Алергени",
    "menu.whyTwoPrices":
      "Та сама страва на місці має 12 %, а з собою 21 %. Ставка визначається для кожної позиції, а не зберігається на товарі.",

    "staff.heading": "Люди",
    "staff.name": "Ім'я",
    "staff.role": "Роль",
    "staff.add": "Додати людину",
    "staff.nameHint": "Як її покаже каса — «Jana N.»",
    "staff.pin": "PIN",
    "staff.newPin": "Новий PIN",
    "staff.pinHint": "Від шести до дванадцяти цифр. Повідомте його — більше він ніде не показується.",
    "staff.pinResetHint": "Залиште порожнім, щоб не змінювати PIN.",
    "staff.active": "Працює у нас",
    "staff.added": "Додано. На касах з'явиться після наступної синхронізації.",
    "staff.saved": "Збережено.",
    "staff.savedTills": "Збережено. Каси забудуть старий PIN і роль після наступної синхронізації.",
    "staff.left": "Більше не працюють",
    "staff.count": "{n} у команді",
    "venue.version": "На касах версія",
    "venue.unpublished": "Зміни ще не на касах",
    "venue.publish": "Опублікувати на каси",
    "venue.saved": "Збережено. На каси потрапить після публікації.",
    "venue.published": "Опубліковано — каси завантажать одразу.",
    "venue.company": "Фірма на чеку",
    "venue.guests": "Гості",
    "venue.guestOrdering": "Гості можуть замовляти з телефону",
    "venue.guestOrderingLead": "QR-код на рахунку завжди показує рахунок. Замовляти з нього можна, лише коли це ввімкнено — діє одразу, без публікації.",
    "venue.companyLead": "Так, як має бути на податковому документі.",
    "venue.legalName": "Назва фірми",
    "venue.registrationId": "IČO",
    "venue.vatId": "DIČ (лише платник ПДВ)",
    "venue.address": "Юридична адреса",
    "venue.eic": "EET: DIČ платника",
    "venue.unit": "EET: номер закладу",
    "venue.tables": "Столи",
    "venue.addTable": "Додати стіл",
    "venue.noArea": "Без зони",
    "venue.seats": "місць",
    "venue.seatsLabel": "Кількість місць",
    "venue.newTable": "Новий стіл",
    "venue.editTable": "Редагувати стіл",
    "venue.ownerOnly": "Змінити може лише власник.",
    "venue.seats.one": "місце",
    "venue.seats.few": "місця",
    "venue.seats.many": "місць",
    "venue.seats.other": "місця",
    "venue.tableName": "Назва",
    "venue.area": "Зона",
    "venue.tableActive": "Використовується",
    "devices.heading": "Пристрої",
    "devices.pair": "Підключити пристрій",
    "devices.codeFor": "Код для",
    "devices.codeHow": "На новому пристрої відкрийте застосунок і введіть його.",
    "devices.codeUntil": "Діє один раз, до",
    "devices.done": "Готово",
    "devices.name": "Назва",
    "devices.namePlaceholder": "напр. Планшет біля бару",
    "devices.kind": "Тип",
    "devices.till": "Каса",
    "devices.kitchen": "Кухонний екран",
    "devices.station": "Станція",
    "devices.makeCode": "Створити код",
    "devices.unpair": "Відключити",
    "devices.place": "Де стоїть",
    "devices.atTables": "Біля столів",
    "devices.atCounter": "Біля прилавка — оплата одразу, номер замовлення",
    "devices.pickupScreen": "Видача — екран із номерами для гостей",
    "devices.counterChip": "Прилавок {prefix}",
    "devices.toCounter": "До прилавка",
    "devices.toTables": "До столів",
    "venue.noTables": "Столів ще немає. Додайте перший — каси побачать їх після публікації.",
    "devices.none": "Пристроїв ще немає. Першу касу підключіть за кодом.",

    // --- The accountant's file (Pohoda) -----------------------------------
    "tender.CASH": "Готівка",
    "tender.CARD": "Картка",
    "tender.MEAL_VOUCHER": "Талони на харчування",
    "tender.GIFT_CARD": "Подарункова картка",
    "tender.HOUSE_ACCOUNT": "На рахунок закладу",
    "tender.QR_TRANSFER": "QR-оплата",
    "tender.VOUCHER": "Ваучер",
    "tender.ROOM_CHARGE": "На номер",
    "tender.EXTERNAL": "Інша оплата",
    "pohoda.heading": "Для бухгалтерії",
    "pohoda.lead": "Виручка як файл для Pohoda: на кожен день і спосіб оплати — один прибутковий касовий документ, розділений за ставками ПДВ.",
    "pohoda.month": "Місяць",
    "pohoda.toYesterday": "до вчора",
    "pohoda.download": "Завантажити для Pohoda",
    "pohoda.downloaded": "Завантажено. Імпортуйте файл у Pohoda через XML-імпорт.",
    "pohoda.noCashAccount": "У Pohoda не вказано касу для:",
    "pohoda.fixInSettings": "Додайте її в Налаштуваннях.",
    "pohoda.noRegistrationId": "У компанії немає IČO. Додайте його в Налаштуваннях.",
    "pohoda.nothing": "За цей період нічого не продано.",
    "pohoda.dayNotClosed": "Сьогоднішній день ще не закінчився.",
    "pohoda.notSetUp": "Налаштування для Pohoda недійсні. Перевірте їх у Налаштуваннях.",
    "pohoda.training": "Це навчальний заклад: тут нічого не обліковується, тож для Pohoda нічого експортувати.",
    "pohoda.settings": "Pohoda",
    "pohoda.settingsLead": "Куди в Pohoda проводиться кожен спосіб оплати. Скорочення кас і передконтацій перепишіть точно так, як вони є в Pohoda, — найкраще в бухгалтерії.",
    "pohoda.cashAccount": "Каса (Pokladna)",
    "pohoda.accounting": "Передконтація (předkontace)",
    "pohoda.optional": "необов’язково",
    "pohoda.saved": "Збережено.",
    "pohoda.tooLong": "Не більше 19 символів.",
    "devices.confirmUnpair": "Відключити «{name}»? Він перестане працювати при наступному звʼязку з сервером.",
    "settings.heading": "Налаштування",
    "settings.venue": "Заклад",
    "settings.timezone": "Часовий пояс",
    "settings.dayStart": "Торговий день починається",
    "settings.fiscalId": "Податкова реєстрація",
    "settings.devices": "Каси",
    "settings.language": "Мова",

    "preview.badge": "Готуємо",
    "preview.note":
      "Це ескіз екрана, який ще не побудовано. Нічого з цього не є справжніми даними.",
    "preview.stock": "Склад",
    "preview.purchasing": "Рахунки від постачальників",
    "preview.rota": "Графіки змін",
    "preview.analytics": "Аналітика",
    "preview.loyalty": "Лояльність",
    "modules.title": "Модулі",
    "modules.lead": "Що використовує цей заклад. Продаж, чеки, каса та EET увімкнені завжди; решту вмикаєте в межах свого тарифу.",
    "modules.plan": "Тариф: {plan}",
    "modules.noPlan": "Без тарифу — лише продаж і чеки.",
    "modules.planLapsed": "Тариф закінчився, додаткові модулі вимкнено. Продаж триває.",
    "modules.preset": "Тип закладу",
    "modules.custom": "Власний вибір",
    "modules.save": "Зберегти модулі",
    "modules.saved": "Збережено. Каси й екрани побачать це за кілька секунд.",
    "modules.notInPlan": "Немає в тарифі",
    "modules.needs": "Потребує: {modules}",
    "modules.always": "Завжди ввімкнено",
    "modules.soon": "Готуємо",
    "modules.noServiceMode": "Залиште столи, прилавок або обидва — інакше продавати неможливо.",
    "preset.cafe": "Кав’ярня",
    "preset.restaurant": "Ресторан",
    "preset.cafe-restaurant": "Кав’ярня + ресторан",
    "preset.pub": "Паб / бар",
    "preset.bakery": "Пекарня / на винос",
    "preset.food-truck": "Фудтрак",
    "module.sales.name": "Продаж і чеки",
    "module.sales.lead": "Каса, оплати, чеки, зміни, EET, меню, люди та виторг.",
    "module.tables.name": "Столи",
    "module.tables.lead": "План залу, рахунки за столом, перенесення й поділ.",
    "module.counter.name": "Прилавок",
    "module.counter.lead": "Продаж біля прилавка: оплата одразу, номер замовлення, тут або з собою.",
    "module.kitchen-display.name": "Кухонний екран",
    "module.kitchen-display.lead": "Замовлення на екрані на кухні й у барі, алергени, видача.",
    "module.kitchen-printing.name": "Друк на кухню",
    "module.kitchen-printing.lead": "Замовлення на принтер на кухні й у барі.",
    "module.courses.name": "Подачі",
    "module.courses.lead": "Закуски одразу, основні страви — коли покличуть.",
    "module.guest-ordering.name": "Замовлення від столу",
    "module.guest-ordering.lead": "Гість замовляє з телефону через QR-код на столі.",
    "module.public-menu.name": "Меню на сайті",
    "module.public-menu.lead": "Публічне меню з фото й алергенами кількома мовами.",
    "module.online-ordering.name": "Онлайн-замовлення на виніс",
    "module.online-ordering.lead": "Замовлення й оплата наперед, видача біля прилавка у вибраний час.",
    "module.reservations.name": "Бронювання",
    "module.reservations.lead": "Бронювання столів із сайту й телефоном, на плані залу.",
    "module.stock.name": "Склад",
    "module.stock.lead": "Сировина, рецептури, списання продажем, приймання, інвентаризації, собівартість.",
    "module.accounting.name": "Бухгалтерія",
    "module.accounting.lead": "Виторг як файл для Pohoda.",
    "module.loyalty.name": "Програма лояльності",
    "module.loyalty.lead": "Бали й винагороди для постійних гостей.",
    "module.crm.name": "Гості",
    "module.crm.lead": "Хто до вас приходить і як часто.",
    "module.foodsafety.name": "Безпека харчових продуктів",
    "module.foodsafety.lead": "Записи HACCP і перевірки.",

    // --- Stock (the Sklad screens) ---------------------------------------------
    "nav.reservations": "Бронювання",
    "res.title": "Бронювання",
    "res.lead": "Хто прийде сьогодні, наскільки зайнято у який час, і бронювання телефоном.",
    "res.prev": "Попередній день",
    "res.next": "Наступний день",
    "res.today": "Сьогодні",
    "res.covers": "Гостей",
    "res.bookings": "Бронювань",
    "res.capacity": "Місць одночасно",
    "res.strip": "Завантаженість протягом вечора",
    "res.none": "На цей день бронювань немає.",
    "res.time": "Час",
    "res.name": "Ім'я",
    "res.party": "Осіб",
    "res.phone": "Телефон",
    "res.table": "Стіл",
    "res.note": "Примітка",
    "res.source": "Звідки",
    "res.status": "Стан",
    "res.source.ONLINE": "сайт",
    "res.source.PHONE": "телефон",
    "res.source.WALK_IN": "без бронювання",
    "res.status.BOOKED": "прийде",
    "res.status.SEATED": "посаджені",
    "res.status.NO_SHOW": "не прийшли",
    "res.status.CANCELLED": "скасовано",
    "res.seat": "Посадити",
    "res.noShow": "Не прийшли",
    "res.cancel": "Скасувати",
    "res.restore": "Повернути",
    "res.noTable": "— без столу —",
    "res.add": "Нове бронювання",
    "res.addHint": "Телефоном або для гостя, який зайшов.",
    "res.save": "Записати",
    "res.full": "На цей час усе зайнято. Записати все одно?",
    "res.force": "Записати все одно",
    "res.closed": "У цей час у вас зачинено.",
    "res.saved": "Записано.",
    "nav.history": "Історія змін",
    "nav.printers": "Принтери",
    "print.lead": "Що отримав кожен принтер — і що не надрукувалося та чому.",
    "print.none": "Принтерів поки немає. Кухня має екрани, а чеки показує каса.",
    "print.empty": "За останні 24 години нічого.",
    "print.unrouted": "Без принтера",
    "print.transport.SCREEN": "На екрані",
    "print.transport.HUB": "Принтер у мережі закладу",
    "print.transport.CLOUDPRNT": "Принтер Star через CloudPRNT",
    "print.columns": "{n} символів у рядку",
    "print.receipts": "чеки з каси",
    "print.status.PRINTED": "Надруковано {at}",
    "print.status.QUEUED": "Очікує на принтер",
    "print.status.STALE": "Не надруковано: відправлено о {fired}, до принтера дійшло лише о {at}",
    "print.status.UNROUTED": "Не надруковано: жоден принтер не друкує для {station}",
    "print.copy": "Копія",
    "print.reprint": "Надрукувати знову",
    "print.reprinted": "Копію надіслано на принтер.",
    "print.setup": "Налаштувати принтер",
    "print.form.name": "Назва",
    "print.form.transport": "Підключення",
    "print.form.columns": "Ширина рулону",
    "print.form.stations": "Друкує замовлення для",
    "print.form.receipts": "Друкує чеки з каси",
    "print.form.active": "Увімкнено",
    "print.form.save": "Зберегти принтер",
    "print.saved": "Збережено — каси вже знають.",
    "print.forbidden": "Принтери бачать керівники зміни й вище.",
    "audit.printer.saved": "Принтер {subject} налаштовано",
    "audit.print.reprinted": "Копія на принтері {subject}",
    "audit.n.lines.one": "{n} позиція",
    "audit.n.lines.few": "{n} позиції",
    "audit.n.lines.many": "{n} позицій",
    "audit.n.lines.other": "{n} позиції",
    "audit.n.seats.one": "{n} місце",
    "audit.n.seats.few": "{n} місця",
    "audit.n.seats.many": "{n} місць",
    "audit.n.seats.other": "{n} місця",
    "audit.n.changes.one": "{n} зміна",
    "audit.n.changes.few": "{n} зміни",
    "audit.n.changes.many": "{n} змін",
    "audit.n.changes.other": "{n} зміни",
    "audit.title": "Історія змін",
    "audit.lead": "Хто що змінив в офісі — ціни, люди, пристрої, склад. Запис неможливо змінити непомітно.",
    "audit.intact": "Запис цілісний — перевірено: {n}.",
    "audit.broken": "Запис змінено: зміна № {n} не відповідає відбитку. Зв'яжіться з нами.",
    "audit.none": "За цей період нічого не змінювали.",
    "audit.when": "Коли",
    "audit.who": "Хто",
    "audit.what": "Що",
    "audit.system": "Система",
    "audit.forbidden": "Історію змін бачать керівник і власник.",
    "audit.menu.item.created": "Нова страва {subject} за {price}",
    "audit.menu.item.changed": "Зміна страви {subject}",
    "audit.menu.item.priced": "Ціна страви {subject}: {before} → {after}",
    "audit.menu.published": "Меню надіслано на каси (версія {version})",
    "audit.staff.added": "Новий член команди: {subject}",
    "audit.staff.changed": "Зміни у {subject}",
    "audit.staff.left": "{subject} більше у нас не працює",
    "audit.staff.pin.reset": "Новий PIN для {subject}",
    "audit.device.pairing.issued": "Код для підключення нового пристрою",
    "audit.device.unpaired": "Пристрій відключено",
    "audit.device.service.changed": "Пристрій перемкнено: {mode}",
    "audit.mode.COUNTER": "стійка {prefix}",
    "audit.mode.TABLE": "до столів",
    "audit.venue.identity.changed": "Дані компанії змінено",
    "audit.venue.table.saved": "Стіл {subject}: {seats}",
    "audit.venue.published": "Налаштування надіслано на каси (версія {version})",
    "audit.venue.modules.changed": "Модулі змінено",
    "audit.venue.public.changed": "Публічну сторінку змінено",
    "audit.stock.received": "Надходження: {lines} на {price}",
    "audit.stock.wasted": "Списання: {lines}",
    "audit.stock.counted": "Інвентаризація: {lines}",
    "audit.stock.item.saved": "Інгредієнт {subject} змінено",
    "audit.stock.supplier.saved": "Постачальник {subject}",
    "audit.stock.recipe.versioned": "Рецептура {subject}, версія {version}",
    "audit.stock.option-delta.versioned": "Що змінює вибір гостя, версія {version}",
    "audit.booking.made": "Бронювання на {party}",
    "audit.booking.forced": "Бронювання на {party} понад місткість",
    "audit.booking.changed": "Бронювання: {status}",
    "nav.stock": "Склад",
    "stock.tab.overview": "Огляд",
    "stock.tab.items": "Сировина",
    "stock.tab.recipes": "Рецептури",
    "stock.tab.receive": "Надходження",
    "stock.tab.count": "Інвентаризація",
    "stock.tab.waste": "Списання",
    "stock.tab.suppliers": "Постачальники",
    "stock.value": "Вартість запасів",
    "stock.valueHint": "За середніми закупівельними цінами без ПДВ.",
    "stock.foodCost": "Фудкост · частка сировини",
    "stock.foodCostHint": "Вартість сировини проданих страв як частка їхньої виручки без ПДВ — лише страви з рецептурою.",
    "stock.period.today": "Сьогодні",
    "stock.period.week": "7 днів",
    "stock.period.month": "30 днів",
    "stock.running": "Закінчується",
    "stock.runningNone": "Нічого не закінчується.",
    "stock.runningLine": "залишилось {left}, замовити нижче {min}",
    "stock.byDish": "За стравами",
    "stock.dish": "Страва",
    "stock.sold": "Продано",
    "stock.takings": "Виручка без ПДВ",
    "stock.cost": "Сировина на",
    "stock.share": "Частка",
    "stock.noSales": "За цей період не продано нічого з рецептурою.",
    "stock.unmapped": "Продажі без рецептури",
    "stock.unmappedHint": "Для цих позицій склад нічого не списує. Додайте їм рецептуру — вона діє з моменту збереження.",
    "stock.timesSold": "продано {n}×",
    "stock.writeRecipe": "Написати рецептуру",
    "stock.problems": "Що склад не зміг записати",
    "stock.addItem": "Додати сировину",
    "stock.item.name": "Назва",
    "stock.item.category": "Група",
    "stock.item.categoryHint": "Напр. Бар, Кухня, Льох — так сировина впорядкована й в інвентаризації.",
    "stock.item.onHand": "На складі",
    "stock.item.unitCost": "Ціна",
    "stock.item.value": "Вартість",
    "stock.item.unit": "Ведеться в",
    "stock.item.unitHint": "Після першого руху на складі одиницю змінити не можна.",
    "stock.item.reorder": "Замовити, коли впаде нижче",
    "stock.item.standardCost": "Ціна до першого надходження",
    "stock.item.standardCostHint": "Kč без ПДВ за {unit}. Потім рахується середнє з надходжень.",
    "stock.item.active": "Використовуємо",
    "stock.item.low": "закінчується",
    "stock.unit.g": "кілограмах (кг)",
    "stock.unit.ml": "літрах (л)",
    "stock.unit.ea": "штуках (шт)",
    "stock.noItems": "Сировини ще немає. Додайте першу — наприклад, каву чи молоко.",
    "stock.perUnit": "за {unit}",
    "stock.saved": "Збережено.",
    "stock.recipe.lead": "Що використовує одна порція. Нова версія діє з моменту збереження; проданого раніше це не змінює.",
    "stock.recipe.portionCost": "Порція коштує",
    "stock.recipe.price": "Ціна без ПДВ",
    "stock.recipe.none": "без рецептури",
    "stock.recipe.uncosted": "Ще без ціни: {items}",
    "stock.recipe.ingredient": "Сировина",
    "stock.recipe.quantity": "Кількість",
    "stock.recipe.save": "Зберегти нову версію",
    "stock.recipe.version": "версія {n}",
    "stock.recipe.saved": "Збережено. Діє для всього, що продаватиметься відтепер.",
    "stock.options": "Вибір гостей",
    "stock.options.lead": "Що вибір змінює на складі, на одну штуку: вівсяне молоко — це +0,2 л вівсяного і −0,2 л звичайного.",
    "stock.options.none": "нічого не змінює на складі",
    "stock.options.signedHint": "Додатна кількість додає, від'ємна — віднімає.",
    "stock.receive.supplier": "Постачальник",
    "stock.receive.noSupplier": "— без постачальника —",
    "stock.receive.document": "Номер накладної або рахунку",
    "stock.receive.netTotal": "Разом без ПДВ (Kč)",
    "stock.receive.submit": "Оприбуткувати",
    "stock.receive.done": "Оприбутковано. Середні ціни перераховано.",
    "stock.recent": "Останні рухи",
    "stock.line.add": "Ще рядок",
    "stock.line.remove": "Прибрати рядок",
    "stock.line.pick": "— оберіть сировину —",
    "stock.line.incomplete": "Кожен рядок потребує сировини й кількості.",
    "stock.pending": "Записано; склад оновиться за кілька секунд.",
    "stock.count.last": "Остання інвентаризація · {when}",
    "stock.count.lastExact": "Усе збіглося.",
    "stock.count.lead": "Порахуйте, що є на полиці, незалежно від того, що каже система. Різницю побачите ще до запису.",
    "stock.count.counted": "Пораховано",
    "stock.count.expected": "За системою",
    "stock.count.variance": "Різниця",
    "stock.count.check": "Перевірити різницю",
    "stock.count.post": "Записати інвентаризацію",
    "stock.count.back": "Змінити",
    "stock.count.done": "Інвентаризацію записано. Склад відповідає порахованому.",
    "stock.count.total": "Загальна різниця",
    "stock.count.empty": "Введіть хоча б одну кількість.",
    "stock.waste.reason": "Причина",
    "stock.waste.submit": "Списати",
    "stock.waste.done": "Списано.",
    "stock.reason.SPOILED": "Зіпсовано",
    "stock.reason.DAMAGED": "Пошкоджено",
    "stock.reason.EXPIRED": "Прострочено",
    "stock.reason.PREP_ERROR": "Помилка на кухні",
    "stock.reason.RETURNED_BY_GUEST": "Повернуто гостем",
    "stock.reason.STAFF_MEAL": "Харчування персоналу",
    "stock.reason.VOID_AFTER_FIRE": "Скасовано після відправки на кухню",
    "stock.reason.COUNT": "Інвентаризація",
    "stock.reason.LATE_BEFORE_COUNT": "Продаж з часу до інвентаризації",
    "stock.kind.RECEIPT": "Надходження",
    "stock.kind.SALE_DEPLETION": "Продаж",
    "stock.kind.WASTE": "Списання",
    "stock.kind.STAFF_MEAL": "Персонал",
    "stock.kind.COUNT_ADJUSTMENT": "Інвентаризація",
    "stock.supplier.add": "Додати постачальника",
    "stock.supplier.registration": "Ідентифікаційний номер",
    "stock.supplier.none": "Постачальників ще немає.",
    "today.lowStock": "Закінчується: {items}",
    "stock.options.edit": "Змінити",
    "stock.unmappedMore": "і ще {n}",
    "approvals.title": "Схвалено без повноважень",
    "approvals.lead": "Каса мала це відхилити. Продаж чинний, але хтось має це перевірити.",
    "approvals.act.VOID_SENT": "Скасування страви, яку вже мала кухня",
    "approvals.act.COMP": "Безкоштовно",
    "approvals.act.DISCOUNT": "Знижка",
    "approvals.act.REFUND": "Повернення коштів",
    "approvals.problem.NOT_PERMITTED": "{name} не має на це повноважень",
    "approvals.problem.UNKNOWN_APPROVER": "схвалила невідома особа ({name})",
    "approvals.problem.INACTIVE_APPROVER": "{name} тут більше не працює",
    "approvals.open": "Відкрити рахунок",
    "checks.pickup": "Видача {code}",
    "tender.ONLINE": "Онлайн-оплата",
    "public.title": "Публічна сторінка та замовлення",
    "public.lead": "Де гості вас знайдуть: меню, години роботи та замовлення на винос.",
    "public.slug": "Адреса сторінки",
    "public.slugHint": "Малі латинські літери, цифри та дефіси — наприклад, «lipa-vinohrady».",
    "public.notYet": "Сторінка ще не має адреси.",
    "public.open": "Відкрити сторінку",
    "public.tagline": "Короткий опис",
    "public.address": "Адреса",
    "public.phone": "Телефон",
    "public.hours": "Години роботи",
    "public.hoursHint": "Час закриття раніше за час відкриття означає після півночі.",
    "public.closed": "зачинено",
    "public.addRange": "+ ще один час",
    "public.day.mon": "Понеділок",
    "public.day.tue": "Вівторок",
    "public.day.wed": "Середа",
    "public.day.thu": "Четвер",
    "public.day.fri": "П'ятниця",
    "public.day.sat": "Субота",
    "public.day.sun": "Неділя",
    "public.online": "Приймаємо онлайн-замовлення",
    "public.onlineHint": "Вимкнення одразу призупиняє замовлення — наприклад, коли все зайнято.",
    "public.prep": "Приготування (хвилини)",
    "public.slot": "Час отримання кожні (хвилин)",
    "public.bookings": "Приймаємо бронювання онлайн",
    "public.bookingsHint": "Гості бронюють столик на вашій сторінці. Телефоном можна бронювати завжди.",
    "public.capacity": "Гостей одночасно",
    "public.duration": "Тримаємо столик (хвилини)",
    "public.bookingSlot": "Час бронювань кожні (хвилин)",
    "public.maxParty": "Найбільша група онлайн",
    "public.daysAhead": "Скільки днів наперед",
    "public.notice": "Щонайпізніше заздалегідь (хвилини)",
    "public.saved": "Збережено. Сторінка зміниться одразу.",
    "menu.guests": "Для гостей онлайн",
    "menu.photo": "Фото",
    "menu.photoUpload": "Завантажити фото",
    "menu.photoRemove": "Прибрати фото",
    "menu.photoHint": "Ми зменшуємо його в браузері — надсилається лише невелика копія.",
    "menu.photoFailed": "Не вдалося завантажити фото.",
    "menu.nameEn": "Назва англійською",
    "menu.descriptionEn": "Опис англійською",
    "menu.dietary": "Підходить для",
    "menu.diet.VEGAN": "веганів",
    "menu.diet.VEGETARIAN": "вегетаріанців",
    "menu.diet.GLUTEN_FREE": "без глютену",
    "menu.diet.LACTOSE_FREE": "без лактози",
    "menu.diet.SPICY": "гостре",
    "menu.featured": "Рекомендуємо — вгорі сторінки",
  },
};
